import React from "react";
import { Box, Typography, Container, Grid } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { motion } from "framer-motion";
import { Circle } from "lucide-react";

const toolCategories = [
  {
    title: "Patient Experience Tools",
    items: [
      "Smart Video Reports/Websites",
      "Personalized Test Explainers",
      "AI-Driven Health Education",
      "Multilingual Display",
    ],
    highlighted: false,
  },
  {
    title: "Operational Intelligence Tools",
    items: [
      "Real-Time Analytics Dashboard",
      "Lab Performance Insights",
      "Data Exchange & Monitoring",
      "Enterprise-Ready Reporting",
    ],
    highlighted: true,
  },
  {
    title: "Doctor Enablement Tools",
    items: [
      "WhatsApp Chatbot",
      "Instant Result Summary",
      "Clinical View Dashboard(SSL/HTTPS Certificate)",
      "Risk Score Preview Builder",
    ],
    highlighted: false,
  },
];

const SectionContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(12),
  backgroundColor: "rgba(245, 242, 240, 0.3)",
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
  },
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(10),
  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(6),
  },
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(5),
  },
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontSize: "2rem",
  fontWeight: 700,
  letterSpacing: "-0.01em",
  [theme.breakpoints.down("md")]: {
    fontSize: "1.75rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.5rem",
  },
}));

const TitleForeground = styled("span")({
  color: "#1D47A4",
});

const TitlePrimary = styled("span")({
  color: "#3567D6",
});

const borderGlow = keyframes`
  0%, 100% {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
  50% {
    box-shadow: 0 8px 32px rgba(53, 103, 214, 0.2);
  }
`;

const ToolCard = styled(motion.div)(({ theme, highlighted }) => ({
  borderRadius: "1.25rem",
  transition: "all 0.3s ease",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  ...(highlighted
    ? {
        backgroundColor: "#022674",
        color: "#FFFFFF",
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
        marginTop: theme.spacing(-2),
        padding: theme.spacing(5),
        minHeight: "110%",
        "&:hover": {
          transform: "translateY(-4px) scale(1.02)",
          boxShadow: "0 24px 72px rgba(0, 0, 0, 0.25)",
        },
        [theme.breakpoints.down("md")]: {
          marginTop: 0,
          padding: theme.spacing(4.5),
          minHeight: "100%",
        },
      }
    : {
        backgroundColor: "#FFFFFF",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        padding: theme.spacing(4.5),
        "&:hover": {
          transform: "translateY(-4px)",
          animation: `${borderGlow} 2s ease-in-out infinite`,
        },
      }),
}));

const CardTitle = styled(Typography)(({ theme, highlighted }) => ({
  fontSize: "1.125rem",
  fontWeight: 600,
  marginBottom: highlighted ? theme.spacing(4) : theme.spacing(3.5),
  color: highlighted ? "#FFFFFF" : "#1D47A4",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.0625rem",
    marginBottom: highlighted ? theme.spacing(3.5) : theme.spacing(3),
  },
}));

const ToolList = styled("ul")(({ theme, highlighted }) => ({
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "flex",
  flexDirection: "column",
  gap: highlighted ? theme.spacing(2.5) : theme.spacing(2),
  flex: 1,
}));

const ToolItem = styled("li")(({ theme, highlighted }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: highlighted ? theme.spacing(3) : theme.spacing(2),
}));

const ToolText = styled(Typography)(({ highlighted }) => ({
  fontSize: "0.875rem",
  lineHeight: highlighted ? 1.6 : 1.4,
  color: highlighted ? "rgba(255, 255, 255, 0.9)" : "#4C4C4C",
}));

const SmartToolsSection = () => {
  return (
    <SectionContainer id="services">
      <Container>
        {/* Section Title */}
        <SectionTitle>
          <TitleText>
            <TitleForeground>Explore Our</TitleForeground>
            <br />
            <TitlePrimary>Smart Engagement Tools</TitlePrimary>
          </TitleText>
        </SectionTitle>

        {/* Tools Cards */}
        <Grid container spacing={{ xs: 2.5, sm: 3 }} alignItems="stretch">
          {toolCategories.map((category, index) => (
            <Grid 
              item 
              xs={12} 
              md={4} 
              key={category.title} 
              sx={{ 
                display: "flex",
              }}
            >
              <ToolCard
                highlighted={category.highlighted}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                style={{ width: "100%" }}
              >
                <CardTitle highlighted={category.highlighted}>
                  {category.title}
                </CardTitle>
                <ToolList highlighted={category.highlighted}>
                  {category.items.map((item) => (
                    <ToolItem key={item} highlighted={category.highlighted}>
                      <Circle
                        style={{
                          width: "0.5rem",
                          height: "0.5rem",
                          marginTop: "0.5rem",
                          flexShrink: 0,
                          fill: category.highlighted ? "#FFFFFF" : "#3567D6",
                          color: category.highlighted ? "#FFFFFF" : "#3567D6",
                        }}
                      />
                      <ToolText highlighted={category.highlighted}>
                        {item}
                      </ToolText>
                    </ToolItem>
                  ))}
                </ToolList>
              </ToolCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </SectionContainer>
  );
};

export default SmartToolsSection;

